import { App } from "@huub-web/ui";
import { type FC, type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { lightTheme } from "@huub-web/ui/theme/css/lightTheme.css";
import { darkTheme } from "@huub-web/ui/theme/css/darkTheme.css";
import { univeTheme } from "@huub-web/ui/theme/css/univeTheme.css";
import { deHypothekerTheme } from "@huub-web/ui/theme/css/deHypothekerTheme.css";
import { ingTheme } from "@huub-web/ui/theme/css/ingTheme.css";
import { rabobankTheme } from "@huub-web/ui/theme/css/rabobankTheme.css";
import { quionTheme } from "@huub-web/ui/theme/css/quionTheme.css";
import { nationaleNederlandenTheme } from "@huub-web/ui/theme/css/nationaleNederlandenTheme.css";
import { vanLanschotKempenTheme } from "@huub-web/ui/theme/css/vanLanschotKempenTheme.css";
import { verenigingEigenHuisTheme } from "@huub-web/ui/theme/css/verenigingEigenHuisTheme.css";
import { nibcTheme } from "@huub-web/ui/theme/css/nibcTheme.css";
import { muntTheme } from "@huub-web/ui/theme/css/muntTheme.css";
import { hypotheekvisieTheme } from "@huub-web/ui/theme/css/hypotheekvisieTheme.css";
import { obvionTheme } from "@huub-web/ui/theme/css/obvionTheme.css";
import { vistaTheme } from "@huub-web/ui/theme/css/vistaTheme.css";
import { asrTheme } from "@huub-web/ui/theme/css/asrTheme.css";
import { anwbTheme } from "@huub-web/ui/theme/css/anwbTheme.css";
import { eviVanLanschotTheme } from "@huub-web/ui/theme/css/eviVanLanschotTheme.css";
import { allianzTheme } from "@huub-web/ui/theme/css/allianzTheme.css";
import { fnvTheme } from "@huub-web/ui/theme/css/fnvTheme.css";
import { randstadTheme } from "@huub-web/ui/theme/css/randstadTheme.css";
import { achmeaTheme } from "@huub-web/ui/theme/css/achmeaTheme.css";
import { fundaTheme } from "@huub-web/ui/theme/css/fundaTheme.css";
import { nvmTheme } from "@huub-web/ui/theme/css/nvmTheme.css";
import { abnamroTheme } from "@huub-web/ui/theme/css/abnamroTheme.css";
import { domekTheme } from "@huub-web/ui/theme/css/domekTheme.css";

import { useAppSettings } from "../../hooks/useAppSettings";
import { useFavicon } from "../../hooks/useFavicon";
import { ThemeSlotProvider } from "../ThemeSlotProvider/ThemeSlotProvider";
import { switchWhitelabel } from "../../i18n";

import { viteLayoutStyle } from "./UIProvider.css";

interface UIProviderProps {
  children: ReactNode;
}

export const UIProvider: FC<UIProviderProps> = ({ children }) => {
  const { theme } = useAppSettings();
  const { t } = useTranslation();
  /**
   * Tracks which theme's brand overlay is currently applied in i18n.
   * Initialized to VITE_THEME (what initI18n() used) rather than the
   * React state value, so we correctly detect when the localStorage-persisted
   * theme differs from the build-time theme after a page refresh.
   */
  const appliedI18nThemeRef = useRef(
    (import.meta.env.VITE_THEME as string) || "light",
  );

  useFavicon({ theme });

  useEffect(() => {
    document.title = t("vite.documentTitle");
  }, [t, theme]);

  useEffect(() => {
    if (theme === appliedI18nThemeRef.current) {
      return;
    }
    appliedI18nThemeRef.current = theme;
    switchWhitelabel({ theme }).then(() => {
      document.title = t("vite.documentTitle");
    });
  }, [theme, t]);

  return (
    <App
      className={viteLayoutStyle}
      theme={theme}
      themeClasses={{
        light: lightTheme,
        dark: darkTheme,
        unive: univeTheme,
        deHypotheker: deHypothekerTheme,
        ing: ingTheme,
        rabobank: rabobankTheme,
        quion: quionTheme,
        nationaleNederlanden: nationaleNederlandenTheme,
        vanLanschotKempen: vanLanschotKempenTheme,
        verenigingEigenHuis: verenigingEigenHuisTheme,
        nibc: nibcTheme,
        munt: muntTheme,
        hypotheekvisie: hypotheekvisieTheme,
        obvion: obvionTheme,
        vista: vistaTheme,
        asr: asrTheme,
        anwb: anwbTheme,
        eviVanLanschot: eviVanLanschotTheme,
        allianz: allianzTheme,
        fnv: fnvTheme,
        randstad: randstadTheme,
        achmea: achmeaTheme,
        funda: fundaTheme,
        nvm: nvmTheme,
        abnamro: abnamroTheme,
        domek: domekTheme,
      }}
    >
      <ThemeSlotProvider theme={theme}>{children}</ThemeSlotProvider>
    </App>
  );
};
